import { FunctionComponent } from 'react';

import ClearIcon from '@mui/icons-material/Clear';
import { ThemeProvider } from '@mui/material';
import IconButton from '@mui/material/IconButton';

import { globalTheme } from '../../../../shared/theme/theme';
import { ReactComponent as FilterIconBlack } from '../../assets/icons/searchbar-icons/filter-icon.svg';
import { ReactComponent as FilterIcon } from '../../assets/icons/searchbar-icons/filter-icon1x.svg';
import { ReactComponent as CreateIcon } from '../../assets/icons/searchbar-icons/icon-create.svg';
import { searchBarInputTheme } from '../../themes/search-bar-input.theme';
import { useNavigateToCreateRoutePath } from '../../utils/navigate-to-create/navigate-to-create-route-path.util';
import Box from '../ui/Box/Box';
import Button from '../ui/Button/Button';
import Input from '../ui/Input';
import { SearchBarProps } from './models/search-bar-props.model';
import { buttonHeight, buttonWidth } from './variables/sizes';

const SearchBar: FunctionComponent<SearchBarProps> = ({
  createRoutePath,
  searchInputPlaceholder,
  filterButtonClickHandler,
  searchInputChangeHandler,
  searchOnSubmitHandler,
  inputRef,
  clearText,
  areFiltersVisible,
  initialValue,
}: SearchBarProps) => {
  const hidden = areFiltersVisible;
  const handleClick = useNavigateToCreateRoutePath(createRoutePath ?? '/');

  const buttonColor = !hidden
    ? globalTheme.palette.black.main
    : globalTheme.palette.appGrey.lightSilver;
  const buttonIcon = !hidden ? <FilterIconBlack /> : <FilterIcon />;

  return (
    <ThemeProvider theme={searchBarInputTheme}>
      <form onSubmit={searchOnSubmitHandler}>
        <Box
          sx={{
            mb: '36px',
            mt: '36px',
            display: 'flex',
            flexWrap: 'wrap',
          }}
        >
          <Input
            sx={{
              borderRadius: '5px',
              borderColor: theme => theme.palette.appGrey.lightSilver,
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderWidth: '1px',
                },
              },
            }}
            {...(inputRef.current &&
              inputRef.current.value.length > 0 && {
                InputProps: {
                  endAdornment: (
                    <IconButton
                      onClick={clearText}
                      disabled={!inputRef?.current?.value}
                    >
                      <ClearIcon />
                    </IconButton>
                  ),
                },
              })}
            inputRef={inputRef}
            value={initialValue ?? ''}
            fullWidth
            placeholder={searchInputPlaceholder}
            type="text"
            onChange={searchInputChangeHandler}
          />
          {filterButtonClickHandler && (
            <Button
              type="button"
              sx={{
                backgroundColor: theme =>
                  !hidden
                    ? theme.palette.black.main
                    : theme.palette.appWhite.main,
                border: `1px solid ${
                  !hidden
                    ? globalTheme.palette.black.main
                    : globalTheme.palette.appGrey.filtersButtonBorder
                }`,
                '&:hover': {
                  backgroundColor: buttonColor,
                  border: `1px solid ${globalTheme.palette.black.main}`,
                },
                height: buttonHeight,
                width: buttonWidth,
                ml: 1,
                minWidth: 'unset',
              }}
              onClick={filterButtonClickHandler}
            >
              {buttonIcon}
            </Button>
          )}
          {createRoutePath && (
            <Button
              type="button"
              onClick={handleClick}
              sx={{
                backgroundColor: theme => theme.palette.black.main,
                '&:hover': {
                  backgroundColor: theme => theme.palette.black.main,
                },
                height: buttonHeight,
                width: buttonWidth,
                ml: 1,
                minWidth: 'unset',
              }}
            >
              <CreateIcon />
            </Button>
          )}
        </Box>
      </form>
    </ThemeProvider>
  );
};

export default SearchBar;
